'use client'
import { Icon } from '@/components/icons/svg-icons';
import { memberLoignStyles } from './member-login.style';
import { InputFieldBar, InputFieldPassword, inputFieldStyles } from '@/jupiter/components/input-field';
import { InputFieldCode } from '@/jupiter/components/input-field/input-field-code';
import clsx from 'clsx';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, DropdownEvent, DropdownOption } from '@nf/ui';
import { jupiterButton } from 'styled-system/recipes';
import { forgetPasswordService } from '@/services/forget-password.service';
import { AppDownload } from './components/app-download';
import { memberMobileLoignStyles } from './member-mobile-login.style';
import { PromotionButton } from './components/promotion-button';
import { LiveChatButton } from './components/live-chat-button';
import GestureUnlock from '@/jupiter/components/gesture-unlock/gesture-unlock';
import { LogoBanner } from './components/logo-banner';
import { ForgetPassword } from '../forget-password';
import { useLocale, useTranslations } from 'next-intl';
import { useGestureUnlock } from './hook/use-gesture-unlock';
import { useRouter } from '@/navigation';
import { convertApiLocale, convertClientLocale } from '@nf/utils-common/site';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { LoginBanner } from './components/login-banner';
import { SwitchFromEnum } from '@nf/types/site';
import { useMemberLogin } from '@/hooks/use-member-login';
import { signUpService } from '@/services/sign-up.service';

// mobile版本為Page頁面
export const MemberMobileLogin = () => {
	const t = useTranslations('General');
	const supportLanguages = useSupportLanguages();
	const currentLocale = useLocale();
	const router = useRouter();
	const year = new Date().getFullYear();

	const {
		isLogining,
		loginData,
		errorMessage,
		codeImgSrc,
		onPasswordLogin,
		onGestureLogin,
		onChange,
		refetchCodeImgSrc,
		setLoginData,
		initErrorMessage,
		initLoginData,
	} = useMemberLogin();

	useEffect(() => {
		initLoginData();
	}, []);

	useEffect(() => {
		refetchCodeImgSrc();
	}, [refetchCodeImgSrc]);

	const {
		isGestureSwitchEnabled,
		gestureUsersList,
		hadUsedGestureLogin
	} = useGestureUnlock(
		loginData.username,
		(initUserName) => setLoginData(pre => ({ ...pre, username: initUserName }))
	);

	const handleLogin = async () => {
		try {
			await onPasswordLogin(loginData, { forceToLocale: true, switchFrom: SwitchFromEnum.Mobile, enableRedirect: true });
		} catch (e) { }
	}

	// loginType需要在使用者見到畫面之前就決定 
	const [loginType, setLoginType] = useState<'password' | 'gesture'>(() => {
		if (hadUsedGestureLogin) {
			return 'gesture';
		} else {
			return 'password';
		}
	});

	// 若有可以手勢登入則切換, 若沒有則跳出通知
	const toLoginType = (type: 'password' | 'gesture') => {
		switch (type) {
			case 'password':
				setLoginType('password');
				initErrorMessage();
				break;
			case 'gesture':
				if (isGestureSwitchEnabled) {
					setLoginType('gesture');
					initErrorMessage();
				} else {
					document.dispatchEvent(new CustomEvent('show-ju-toast-msg', {
						detail: { msg: t('lbl_HintBeforeLogin02') }
					}));
				}
		}
	};

	const checkIsPatternValid = async (selectedPattern: number[]) => {
		try {
			const gestureUnlockData = { ...loginData, password: selectedPattern.join('') };

			const data = await onGestureLogin(gestureUnlockData);
			if (data) {
				return true
			} else {
				throw new Error();
			}
		} catch (e) {
			return false;
		}
	}


	const handleLangChange = (e: DropdownEvent) => {
		const clientLocale = convertClientLocale(e.option.value);
		router.replace('/', { locale: clientLocale });
	};

	const handleUserChange = (e: DropdownEvent) => {
		if (e.option.value === 'createAccount') {
			signUpService.displaySignUpModal(true)
		} else {
			setLoginData((pre) => ({ ...pre, username: e.option.value }));
		}
	};

	const dropdownUserList = useMemo(() => [
		...gestureUsersList.map(user => ({ label: user, value: user })),
		{ label: t('lbl_CreateAccount'), value: 'createAccount' }
	], [gestureUsersList]);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'Enter') {
			handleLogin();
		}
	};

	return (
		<main className={memberLoignStyles.root} onKeyDown={handleKeyDown}>
			<AppDownload />
			<LogoBanner />
			<LoginBanner />
			<div className={clsx(memberMobileLoignStyles.content)}>
				{
					{
						password: (
							<div className={memberMobileLoignStyles.formContainer}>
								<form className={memberMobileLoignStyles.formSection}>
									<div className={memberLoignStyles.inputGroup}>
										<div>
											<InputFieldBar
												className={clsx(inputFieldStyles.inputField, { [inputFieldStyles.inputFieldError]: errorMessage.username })}
												type="text"
												icon={<Icon name="account-user" />}
												id="username"
												placeholder={t('lbl_username')}
												value={loginData.username}
												onChange={onChange}
												required
												autoComplete="off"
											/>
											{errorMessage.username ? <div className={memberLoignStyles.inputErrorText}>{t(errorMessage.username)}</div> : null}
										</div>
										<div>
											<InputFieldPassword
												id={'password'}
												className={clsx(inputFieldStyles.inputField, { [inputFieldStyles.inputFieldError]: errorMessage.password })}
												placeholder={t('lbl_Password')}
												value={loginData.password}
												onChange={onChange}
												required
												autoComplete="off"
											/>
											{errorMessage.password ? <div className={memberLoignStyles.inputErrorText}>{t(errorMessage.password)}</div> : null}
										</div>
										<div>
											<InputFieldCode
												className={clsx(inputFieldStyles.inputField, { [inputFieldStyles.inputFieldError]: errorMessage.validation })}
												id="validation"
												placeholder={t('lbl_code')}
												type="text"
												value={loginData.validation}
												codeImgSrc={codeImgSrc}
												onRest={refetchCodeImgSrc}
												onChange={onChange}
											/>
											{errorMessage.validation ? <div className={memberLoignStyles.inputErrorText}>{t(errorMessage.validation)}</div> : null}
										</div>
									</div>
								</form>
								<div className={memberMobileLoignStyles.secondGroup}>
									<InfoMessage
										content={t('lbl_RememberUsername')}
										check={loginData.rememberMe}
										onChange={onChange}
									/>
									<span className={clsx(memberLoignStyles.forgetPassword, memberLoignStyles.fontNormal)} onClick={() => forgetPasswordService.displayForgetPasswordModal(true)}>
										{t('lbl_forgetPaswordTitle')}?
									</span>
								</div>
								{errorMessage.resError ? <div className={memberMobileLoignStyles.reqErrorText} dangerouslySetInnerHTML={{ __html: errorMessage.resError }} /> : null}
								<div className={memberMobileLoignStyles.buttonContainer}>
									<Button
										className={clsx(
											jupiterButton({ size: 'lg', visual: isLogining ? 'disable' : 'primary' }),
											memberLoignStyles.loginButton
										)}
										onClick={handleLogin}
									>
										{isLogining ? (<Icon className={memberLoignStyles.loadingIcon} name="loading" />) : t('lbl_login')}

									</Button>
									<button className={memberLoignStyles.secondButton} onClick={() => toLoginType('gesture')}>
										<span className={memberMobileLoignStyles.buttonText}>{t('lbl_GestureLogin')}</span>
									</button>
									<button className={memberLoignStyles.secondButton} onClick={() => signUpService.displaySignUpModal(true)}>
										<span className={memberMobileLoignStyles.buttonText}>{t('SIGNUP')}</span>
									</button>
								</div>
							</div>
						),
						gesture: (
							<div className={memberMobileLoignStyles.formContainer}>
								<Dropdown
									variant="right"
									value={loginData.username}
									options={dropdownUserList}
									onChange={handleUserChange}
									valueTemplate={(selectedOption) => <UserValueTemplate selectedOption={selectedOption} />}
									optionTemplate={(selectedOption) => selectedOption.value === 'createAccount' ? <CreateAccount selectedOption={selectedOption} /> : <UserValueTemplate selectedOption={selectedOption} />}
									iconTemplate={() => <Icon name="arrow-down" />}
									className={memberMobileLoignStyles.userDropdown}
								/>

								<div className={memberMobileLoignStyles.unlockWrapper}>
									<div className={memberMobileLoignStyles.reqErrorText}>
										{!!errorMessage.resError ? (
											<span dangerouslySetInnerHTML={{ __html: errorMessage.resError }} />
										) : isLogining ? (
											<Icon className={memberMobileLoignStyles.gestureLoadingIcon} name="loading" />
										) : null}
									</div>
									<GestureUnlock
										type="login"
										isDisable={isLogining}
										checkIsPatternValid={checkIsPatternValid}
									/>
								</div>
								<button type="submit" className={memberLoignStyles.secondButton} onClick={() => toLoginType('password')}>
									<span className={memberMobileLoignStyles.buttonText}>{t('lbl_PasswordLogin')}</span>
								</button>
							</div>
						)
					}[loginType]
				}
				{supportLanguages ? (
					<Dropdown
						variant="right"
						value={currentLocale}
						options={supportLanguages}
						onChange={handleLangChange}
						valueTemplate={(selectedOption) => <LanguageValueTemplate selectedOption={selectedOption} />}
						iconTemplate={() => <Icon name="arrow-down" />}
						className={memberMobileLoignStyles.languageDropdown}
					/>
				) : null}
				<div className={memberMobileLoignStyles.copyRightText}>
					©Copyright {year}.All Rights Reserved.
				</div>
			</div>
			<PromotionButton />
			<LiveChatButton />
			<ForgetPassword />
		</main>
	)
}

const InfoMessage = ({ check, content, onChange }: { check: boolean, content: string, onChange: (e: ChangeEvent<HTMLInputElement>) => void }) => {
	return (
		<>
			<label htmlFor={'rememberMe'} className={memberLoignStyles.checkMessage}>
				{check ?
					<Icon className={clsx(memberLoignStyles.checkIcon, memberLoignStyles.checkActiveIcon)} data-active={true} name="check-box-active-square" /> :
					<Icon className={memberLoignStyles.checkIcon} name="check-box-square" />
				}
				<span className={clsx(memberLoignStyles.messageContent, memberLoignStyles.fontNormal)}>{content}</span>
			</label>
			<input
				style={{ display: 'none' }}
				id="rememberMe"
				type="checkbox"
				onChange={onChange}
			/>
		</>

	)
}

const LanguageValueTemplate = ({ selectedOption }: { selectedOption: DropdownOption }) => (
	<div className={memberMobileLoignStyles.dropdownButton}>
		<Icon name="earth" />
		<span>{selectedOption?.label || 'Language'}</span>
	</div>
)

const UserValueTemplate = ({ selectedOption }: { selectedOption: DropdownOption }) => (
	<div className={memberMobileLoignStyles.userDropdownButton}>
		<Icon name="account-user" className={memberMobileLoignStyles.userIcon} />
		<span>{selectedOption?.label}</span>
	</div>
)

const CreateAccount = ({ selectedOption }: { selectedOption: DropdownOption }) => {
	return (
		<div className={memberMobileLoignStyles.userDropdownButton}>
			<Icon name="account-user-gear" className={memberMobileLoignStyles.userIcon} />
			<span>{selectedOption?.label || 'nf_001'}</span>
			<Icon name="arrow-right" className={memberMobileLoignStyles.creatAccountArrow} />
		</div>
	)
}

const useSupportLanguages = () => {
	const { SiteConfigsData } = useGetSiteConfigs();
	return SiteConfigsData?.SupportLanguages?.map((language) => ({ label: language.Name, value: convertClientLocale(language.IsoCode), ShortCode: language.ShortCode }));
}